/**
 * The one hook a chat screen needs.
 *
 * Joins the durable transcript to the in-flight turn and hands back something a
 * component can render without knowing any of this exists.
 *
 * The idea that holds the whole thing together: **the streaming overlay is derived
 * from the cache, never cleared on a timer.** The bubble showing the streamed answer
 * disappears in the very same commit that the persisted row appears, so there is no
 * window where the answer is on screen twice, and none where it has vanished and not
 * yet come back. Same for the optimistic echo of the user's own message.
 */
import { useMutation } from "@apollo/client/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { AGENT_ACTIONS } from "../../shared/agent/actions";
import type {
  FormActionError,
  FormActionResult,
  FormValues,
  RequestFormOptionsInput,
} from "../../shared/agent/answers/form/formTypes";
import type { AgentChatMessage } from "../../shared/agent/types";
import useAgentSession from "./useAgentSession";
import useAgentTurn from "./useAgentTurn";
import useChatDocuments from "./useChatDocuments";

/** The user's message, echoed locally until the server confirms it. */
type Echo = { runId: string | null; text: string };

/** The step of a session written by the retired interview: read-only, nothing to do. */
const RETIRED_STEP = "retired";

const GENERIC_ACTION_ERROR =
  "No pudimos completar la acción. Vuelve a intentarlo.";
const GENERIC_SEND_ERROR = "No pudimos enviar tu mensaje.";

const failed = (message: string): FormActionResult => ({
  ok: false,
  errors: [{ field: null, message }],
});

/** What `actOnSession` answers with: the job it queued (if any), or why not. */
type ActionPayload = {
  run: { id: string; background: boolean } | null;
  userErrors: { field: string[] | null; message: string }[] | null;
};

/** The `values` an action takes: a JSON object, camelCase keys. */
type ActionValues = FormValues | Record<string, string>;

/** `userErrors` as the field-keyed list a form draws; empty when there were none. */
const toActionErrors = (
  userErrors: ActionPayload["userErrors"] | undefined,
): FormActionError[] =>
  (userErrors ?? []).map((error) => ({
    field: error.field?.[0] ?? null,
    message: error.message,
  }));

const useAgentChat = (sessionId: string | undefined) => {
  const {
    session,
    messages,
    hasAnswerFor,
    hasEchoFor,
    reconcile,
    loadOlder,
    hasOlder,
    loading,
  } = useAgentSession(sessionId);

  const readOnly = session?.step === RETIRED_STEP;
  const formMode = !!session && !readOnly;
  const availableActions = session?.availableActions;
  const can = useCallback(
    (action: string) => !!availableActions?.includes(action),
    [availableActions],
  );
  const { turn, store, stalled } = useAgentTurn(sessionId, reconcile);
  const formLive = useSyncExternalStore(store.subscribeForm, store.getForm);
  /**
   * Bumped whenever the draft changed under the chat without a tool call saying so —
   * a form written it, or the script landed. The draft preview refetches on it.
   */
  const [draftRevision, setDraftRevision] = useState(0);
  useEffect(() => {
    if (formLive.scriptReady) setDraftRevision((revision) => revision + 1);
  }, [formLive.scriptReady]);

  const [echo, setEcho] = useState<Echo | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const documents = useChatDocuments();
  const [actOnSessionMutation] = useMutation(documents.actOnSession);

  /**
   * Every thing a person does in the session is this one mutation. The session it
   * returns lands in the cache under its id, so `step` and `availableActions` are
   * already up to date when the caller continues.
   */
  const actOnSession = useCallback(
    async (
      id: string,
      action: string,
      values?: ActionValues,
    ): Promise<ActionPayload | null | undefined> => {
      const result = await actOnSessionMutation({
        variables: {
          sessionId: id,
          action,
          values: values ? JSON.stringify(values) : undefined,
        },
      });
      return result.data?.agent.actOnSession;
    },
    [actOnSessionMutation],
  );

  /**
   * The answer for this turn is in the cache — hide the streamed copy in the same
   * frame so it is never on screen twice. The turn itself retires once the re-read
   * that follows it has finished (see `useAgentTurn`).
   */
  const settled = hasAnswerFor(turn.runId);
  const echoSettled = hasEchoFor(echo?.runId ?? null);

  // Retire the echo once the server's copy of the user's message arrives.
  useEffect(() => {
    if (echo && echoSettled) setEcho(null);
  }, [echo, echoSettled]);

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || !sessionId) return false;

      setSendError(null);
      setEcho({ runId: null, text: trimmed });

      setSending(true);
      try {
        const payload = await actOnSession(
          sessionId,
          AGENT_ACTIONS.sendMessage,
          {
            text: trimmed,
          },
        );
        const userError = payload?.userErrors?.[0];

        // The backend refuses a second turn while one is active, so this fires for
        // real — two tabs can both believe the chat is idle. Never swallow it.
        if (userError || !payload?.run) {
          setEcho(null);
          setSendError(userError?.message ?? GENERIC_SEND_ERROR);
          store.dispatch({ kind: "SEND_REJECTED" });
          return false;
        }

        const runId = payload.run.id;
        setEcho({ runId, text: trimmed });
        // A no-op if the websocket already adopted this run — RUN_STARTED usually
        // beats this response back, and re-adopting here would wipe the deltas that
        // have already arrived.
        store.dispatch({ kind: "SEND_ACCEPTED", runId, at: Date.now() });
        return true;
      } catch {
        // Nothing was persisted, so drop the echo and let the caller restore the
        // draft into the composer. The error itself is Apollo's English text and a
        // network failure says nothing the person can act on.
        setEcho(null);
        setSendError(GENERIC_SEND_ERROR);
        store.dispatch({ kind: "SEND_REJECTED" });
        return false;
      } finally {
        setSending(false);
      }
    },
    [sessionId, actOnSession, store],
  );

  /**
   * Run one action: the mutation, then adopt the run it queued.
   *
   * The action has already committed once the mutation returns, so the durable rows
   * are there to read — waiting for the stream would leave the card on screen for the
   * length of a model call after the person has already submitted it. The read-back
   * never fails (`reconcile` swallows it), so it cannot turn a committed action into
   * an error.
   *
   * A background run is not a turn: adopting it would lock the composer and show a
   * thinking bubble for a job the person is not waiting on.
   */
  const runAction = useCallback(
    async (
      action: string,
      values?: ActionValues,
    ): Promise<FormActionResult> => {
      if (!sessionId) return failed(GENERIC_ACTION_ERROR);
      setSendError(null);
      try {
        const payload = await actOnSession(sessionId, action, values);
        const errors = toActionErrors(payload?.userErrors);
        if (errors.length) return { ok: false, errors };
        if (!payload) return failed(GENERIC_ACTION_ERROR);
        if (payload.run && !payload.run.background) {
          store.dispatch({
            kind: "SEND_ACCEPTED",
            runId: payload.run.id,
            at: Date.now(),
          });
        }
        setDraftRevision((revision) => revision + 1);
        await reconcile();
        return { ok: true };
      } catch {
        return failed(GENERIC_ACTION_ERROR);
      }
    },
    [sessionId, actOnSession, store, reconcile],
  );

  /** Submit a FORM card with the action it carries (`card.action`). */
  const submitForm = useCallback(
    (action: string, values: FormValues) => runAction(action, values),
    [runAction],
  );

  /**
   * Ask for the options of a select that is computed after its card is shown.
   *
   * The run it queues is background work: the person is still filling the form and
   * must not see a thinking bubble or a locked composer for it. The run comes back
   * flagged `background`, as do its lifecycle events, so the turn store ignores it and
   * nothing is adopted here.
   */
  const requestFormOptions = useCallback(
    async (input: RequestFormOptionsInput): Promise<boolean> => {
      if (!sessionId) return false;
      try {
        const payload = await actOnSession(
          sessionId,
          AGENT_ACTIONS.requestDriverOptions,
          input,
        );
        return !payload?.userErrors?.length && !!payload?.run;
      } catch {
        return false;
      }
    },
    [sessionId, actOnSession],
  );

  const selectConcept = useCallback(
    (conceptId: string) =>
      runAction(AGENT_ACTIONS.selectConcept, { conceptId }),
    [runAction],
  );

  const regenerateConcepts = useCallback(
    () => runAction(AGENT_ACTIONS.regenerateConcepts),
    [runAction],
  );

  const dismissError = useCallback(() => {
    setSendError(null);
    store.dispatch({ kind: "DISMISS_ERROR" });
  }, [store]);

  /** The transcript plus the not-yet-confirmed echo of what the user just typed. */
  const visibleMessages = useMemo<AgentChatMessage[]>(() => {
    if (!echo || echoSettled) return messages;
    return [
      ...messages,
      {
        // Only one optimistic echo ever occupies the `echo` slot at a time, so a
        // constant id is safe — and necessary: keying on `echo.runId` changed the
        // id from "echo:pending" to "echo:<runId>" mid-flight, which made
        // MessageList (keyed on message.id) remount the bubble on every send.
        id: "echo",
        role: "user",
        text: echo.text,
        createdAt: null,
        pending: true,
        legacy: null,
        form: null,
        formAnswer: null,
        concept: null,
      },
    ];
  }, [messages, echo, echoSettled]);

  const isStreaming =
    turn.phase !== "idle" && turn.phase !== "failed" && !settled;

  /**
   * Show a spinner rather than the streamed text.
   *
   * Either the run has not produced anything yet, or we reconnected mid-run and the
   * partial we hold has a hole in it we can never fill. A half-answer that silently
   * starts in the middle of a sentence is worse than an honest spinner.
   */
  const isThinking = isStreaming && (turn.phase === "queued" || turn.desynced);

  /**
   * The composer lock.
   *
   * `isRunning` is the only possible source of truth on first load, before any event
   * has arrived — hence `cache-and-network` on the session query. But once a run
   * terminates locally we must not keep waiting on it: the cached `isRunning` is
   * still true until the refetch lands, and honouring it would leave the composer
   * locked for a needless round trip.
   *
   * `stalled` unlocks a chat whose worker died — the server would otherwise hold
   * `isRunning` true for up to ten minutes. If we unlock too early the backend simply
   * refuses the send and we surface `userErrors`; if we lock too long, the chat is
   * bricked with no way out.
   */
  const locked =
    !stalled &&
    (turn.phase === "queued" ||
      turn.phase === "running" ||
      (turn.phase === "idle" && !!session?.isRunning));

  return {
    sessionId: sessionId ?? "",
    session,
    messages: visibleMessages,
    turn,
    store,
    isStreaming,
    isThinking,
    tools: turn.tools,
    /** A run that failed, or a send that never landed. */
    error: turn.error ?? sendError,
    stalled,
    locked,
    sending,
    loading,
    hasOlder,
    loadOlder,
    send,
    dismissError,
    /** A live session: cards and concepts. */
    formMode,
    /**
     * A retired session (the old interview-style chat): its transcript reads, but
     * nothing can be done in it and there is no widget to answer with.
     */
    readOnly,
    /** Whether the session accepts this action on its current step. */
    can,
    /** What the stream says that no persisted row does yet. See `formLiveReducer`. */
    formLive,
    draftRevision,
    submitForm,
    requestFormOptions,
    selectConcept,
    regenerateConcepts,
  };
};

export default useAgentChat;
